import {
  DEFAULT_APPOINTMENT_DURATION_MINUTES,
  normalizeAppointmentDurationMinutes,
  parseAppointmentDateTime,
} from '@/lib/work-items/appointmentSchedule';

export type AppointmentRecurrenceFrequency = 'daily' | 'weekly' | 'fortnightly' | 'monthly';

export interface AppointmentRecurrenceRule {
  enabled: boolean;
  frequency: AppointmentRecurrenceFrequency;
  intervalCount: number;
  occurrenceCount: number;
}

export interface AppointmentRecurrenceOccurrence {
  sequence: number;
  scheduled_start: string;
  scheduled_end: string;
}

function formatLocalDateTimeValue(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const da = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

function addMonthsPreservingDay(date: Date, monthsToAdd: number): Date {
  const next = new Date(date);
  const originalDay = next.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth() + monthsToAdd);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(originalDay, lastDay));
  return next;
}

function addRecurrenceInterval(date: Date, frequency: AppointmentRecurrenceFrequency, intervalCount: number): Date {
  const normalizedInterval = Math.max(1, intervalCount || 1);
  const next = new Date(date);

  switch (frequency) {
    case 'daily':
      next.setDate(next.getDate() + normalizedInterval);
      return next;
    case 'weekly':
      next.setDate(next.getDate() + normalizedInterval * 7);
      return next;
    case 'fortnightly':
      next.setDate(next.getDate() + normalizedInterval * 14);
      return next;
    case 'monthly':
    default:
      return addMonthsPreservingDay(next, normalizedInterval);
  }
}

export function buildRecurringAppointmentOccurrences(
  scheduledStart: string,
  scheduledEnd: string,
  rule?: Partial<AppointmentRecurrenceRule> | null,
): AppointmentRecurrenceOccurrence[] {
  const start = parseAppointmentDateTime(scheduledStart);
  const end = parseAppointmentDateTime(scheduledEnd);

  if (!start || !end) {
    return [];
  }

  const frequency = rule?.frequency || 'weekly';
  const intervalCount = Math.max(1, rule?.intervalCount || 1);
  const occurrenceCount = Math.max(1, Math.min(52, rule?.occurrenceCount || 1));
  const durationMinutes = normalizeAppointmentDurationMinutes(
    Math.round((end.getTime() - start.getTime()) / 60000),
    DEFAULT_APPOINTMENT_DURATION_MINUTES,
  );

  const occurrences: AppointmentRecurrenceOccurrence[] = [];
  let currentStart = new Date(start);

  for (let sequence = 1; sequence <= occurrenceCount; sequence += 1) {
    const currentEnd = new Date(currentStart.getTime() + durationMinutes * 60000);
    occurrences.push({
      sequence,
      scheduled_start: formatLocalDateTimeValue(currentStart),
      scheduled_end: formatLocalDateTimeValue(currentEnd),
    });

    currentStart = addRecurrenceInterval(currentStart, frequency, intervalCount);
  }

  return occurrences;
}
